import React, { ReactElement, useState } from 'react';
import { Div } from '../../Atoms/Div';
import { Avatar } from '../../Atoms/Avatar';
import { Text } from '../../Atoms/Text';
import { Tooltip } from '../../Atoms/Tooltip';
import { FontIcon } from '../../Atoms/FontIcon';
import {
  IconDefinition,
  faChevronDoubleLeft,
  faChevronDoubleRight,
  faCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { useTheme } from '@mui/material/styles';
// SidebarNav subcomponents
import { Container } from './Container';
import { HoverBlock } from './HoverBlock';
import { MenuLink } from './MenuLink';
import { SubMenuLink } from './SubMenuLink';
import { Overflow } from './Overflow';

type SubMenuItem = {
  url: string;
  title: string | ReactElement<any, any> | undefined;
  exact?: boolean;
  isActive?: () => boolean;
  onClick?: () => void;
  icon?: IconDefinition;
};

type MenuItem = {
  url: string;
  title: string | ReactElement<any, any> | undefined;
  tooltip?: string;
  exact?: boolean;
  isActive?: () => boolean;
  onClick?: () => void;
  icon?: IconDefinition;
  indicator?: boolean;
  indicatorColor?: string;
  submenu?: SubMenuItem[];
  style?: Record<string, string>;
};

export type Scheme = {
  textColor?: string;
  activeLinkColor?: string;
  activeLinkBackground?: string;
  activeLinkBorder?: string;
  linkColor?: string;
  linkHoverBackground?: string;
  linkHoverColor?: string;
  subMenuLinkBackground?: string;
  activeSubMenuLinkBackground?: string;
  background?: string;
  subHeaderIconColor?: string;
  borderColor?: string;
  headerIconColor?: string;
};

export type Props = {
  menuItems: MenuItem[];
  homeUrl?: string;
  headerName?: string;
  onClickHome?: () => void;
  headerIcon?: IconDefinition;
  headerImageUrl?: string;
  subHeaderImageUrl?: string;
  subHeaderName?: string;
  subHeaderUrl?: string;
  scheme?: Scheme;
  onCollapse?: () => void;
  collapsed?: boolean;
  width?: string | number;
  /** location object passed by route */
  location?: any;
  /** history object passed by route; necessary if homeUrl or subHeaderUrl are included */
  history?: any;
};

export const SidebarNav: React.FC<Props> = (props) => {
  const {
    onClickHome,
    homeUrl,
    headerIcon,
    headerImageUrl,
    headerName,
    subHeaderImageUrl,
    subHeaderName,
    subHeaderUrl,
    menuItems,
    onCollapse,
    collapsed,
    width = 210, // default width
    location,
    history,
  } = props;

  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const theme = useTheme();
  const defaultScheme = {
    textColor: theme.palette.text.primary,
    activeLinkColor: theme.palette.secondary.main,
    activeLinkBackground: theme.palette.secondary.dim,
    activeLinkBorder: theme.palette.secondary.main,
    linkColor: theme.palette.text.secondary,
    linkHoverBackground: theme.palette.action.hover,
    linkHoverColor: theme.palette.text.secondary,
    subMenuLinkBackground: theme.palette.background.paper,
    activeSubMenuLinkBackground: theme.palette.action.selected,
    background: theme.palette.background.default,
    subHeaderIconColor: theme.palette.secondary.dim,
    borderColor: theme.palette.divider,
    headerIconColor: theme.palette.common.white,
  };
  const scheme = { ...defaultScheme, ...props.scheme };

  return (
    <Container
      width={isCollapsed ? '60px' : width + 'px'}
      minWidth={isCollapsed ? '60px' : width + 'px'}
      flexDirection="column"
      borderRight={`solid 1px ${scheme.borderColor}`}
      scheme={scheme}
    >
      {headerName && (
        <HoverBlock
          dataTest="hoverblock-header"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          borderBottom={`solid 1px ${scheme.borderColor}`}
          height={isCollapsed ? '80px' : width}
          scheme={scheme}
          onClick={() => {
            if (onClickHome) {
              onClickHome();
            }
            if (homeUrl) {
              history.push(homeUrl);
            }
          }}
        >
          <Div mb={isCollapsed ? 0 : 1}>
            {headerIcon ? (
              <FontIcon
                icon={headerIcon}
                mb={isCollapsed ? 0 : 2}
                color={scheme.headerIconColor}
                size="3x"
              />
            ) : (
              <Avatar
                src={headerImageUrl}
                alt={headerName}
                size={isCollapsed ? '44px' : '66px'}
                color={theme.palette.primary.main}
                textcolor={scheme.headerIconColor}
                fontSize={isCollapsed ? '22px' : '36px'}
                cursor="pointer"
              />
            )}
          </Div>
          <Overflow
            width={isCollapsed ? 0 : 'auto'}
            height={isCollapsed ? 0 : 'auto'}
          >
            <Text
              width="100%"
              variant="h3"
              color={scheme.textColor}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              maxWidth={Number(width) - 20}
              title={headerName}
            >
              {headerName}
            </Text>
          </Overflow>
        </HoverBlock>
      )}
      {subHeaderName && (
        <HoverBlock
          dataTest="hoverblock-subheader"
          p={isCollapsed ? '8px' : '12px'}
          justifyContent="flex-start"
          alignItems="center"
          borderBottom={`solid 1px ${scheme.borderColor}`}
          style={{ position: 'relative' }}
          scheme={scheme}
          onClick={() => (subHeaderUrl ? history.push(subHeaderUrl) : null)}
        >
          <Div mr={isCollapsed ? 0 : 1}>
            <Avatar
              data-test="avatar-subheader"
              src={subHeaderImageUrl}
              variant="rounded"
              size="44px"
              color={scheme.subHeaderIconColor}
              alt={subHeaderName}
            >
              {subHeaderName.charAt(0)}
            </Avatar>
          </Div>
          <Overflow width={isCollapsed ? 0 : 'auto'}>
            <Div display="flex" alignItems="center" width="100%">
              <Text
                variant="h4"
                color={scheme.textColor}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                maxWidth={Number(width) - 64}
                title={subHeaderName}
              >
                {subHeaderName}
              </Text>
            </Div>
          </Overflow>
        </HoverBlock>
      )}
      <Div display="flex" flexDirection="column" flexGrow={1}>
        {menuItems.map((item) => {
          return (
            <React.Fragment key={`menu-${item.url}`}>
              <Tooltip
                placement="right"
                title={
                  item.submenu || !isCollapsed
                    ? undefined
                    : item.tooltip || item.title
                }
                color={theme.palette.primary.main}
                fontColor={theme.palette.common.white}
                shadow="none"
                hideArrow
              >
                <Div
                  display="flex"
                  style={{
                    position: 'relative',
                    cursor: 'pointer',
                    ...item.style,
                  }}
                >
                  <MenuLink
                    data-test="menulink-url"
                    exact={!!item.exact}
                    to={item.url}
                    isActive={item.isActive}
                    onClick={item.onClick}
                    scheme={scheme}
                  >
                    <Div display="flex" alignItems="center" height="100%">
                      {item.icon && (
                        <Div width="16px" mr="15px">
                          <FontIcon icon={item.icon} size="1x" />
                        </Div>
                      )}
                      <Overflow
                        data-test="overflow-menuitem-title"
                        width={isCollapsed ? 0 : 'auto'}
                      >
                        {item.title}
                      </Overflow>
                      {item.indicator && !isCollapsed && (
                        <FontIcon
                          icon={faCircle}
                          color={
                            item.indicatorColor || theme.palette.error.main
                          }
                          size="xs"
                          ml="auto"
                          mr="20px"
                        />
                      )}
                    </Div>
                  </MenuLink>
                </Div>
              </Tooltip>
              {item.submenu &&
                location?.pathname.includes(item.url) &&
                item.submenu.map((submenuItem) => {
                  return (
                    <Tooltip
                      key={`submenu-${submenuItem.url}`}
                      title={!isCollapsed ? undefined : submenuItem.title}
                      placement="right"
                      color={theme.palette.primary.main}
                      fontColor={theme.palette.common.white}
                      shadow="none"
                      hideArrow
                    >
                      <SubMenuLink
                        data-test="submenu-url"
                        to={submenuItem.url}
                        exact={!!submenuItem.exact}
                        isActive={submenuItem.isActive}
                        onClick={submenuItem.onClick}
                        scheme={scheme}
                      >
                        <Div display="flex" alignItems="center" height="100%">
                          {isCollapsed && submenuItem.icon ? (
                            <React.Fragment>
                              <Div width="16px" mr="15px">
                                <FontIcon icon={submenuItem.icon} size="1x" />
                              </Div>
                            </React.Fragment>
                          ) : (
                            <Div data-test="submenuItem-title">
                              <Div width="31px" />
                              {submenuItem.title}
                            </Div>
                          )}
                        </Div>
                      </SubMenuLink>
                    </Tooltip>
                  );
                })}
            </React.Fragment>
          );
        })}
      </Div>
      <HoverBlock
        data-test="hoverblock-collapse"
        p="12px"
        alignItems="center"
        borderTop={`solid 1px ${scheme.borderColor}`}
        onClick={() => {
          setIsCollapsed(!isCollapsed);
          if (onCollapse) {
            onCollapse();
          }
        }}
        scheme={scheme}
      >
        <Div width="16px" ml={1} mr="15px">
          <FontIcon
            icon={isCollapsed ? faChevronDoubleRight : faChevronDoubleLeft}
            size="sm"
            color={scheme.linkColor}
          />
        </Div>
        <Overflow width={isCollapsed ? 0 : 'auto'}>
          <Text variant="body1" color={scheme.linkColor}>
            Collapse Sidebar
          </Text>
        </Overflow>
      </HoverBlock>
    </Container>
  );
};

export default SidebarNav;
